import { ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '@/utils/cn'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, disabled, children, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-[120ms] ease-[var(--ease-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 disabled:cursor-not-allowed'
    
    const variants = {
      primary: 'bg-primary text-white hover:bg-primary-hover active:scale-[0.98] dark:bg-primary dark:text-black dark:hover:bg-primary-hover',
      secondary: 'border border-border bg-transparent text-text hover:bg-bg-alt',
      tertiary: 'bg-transparent text-text hover:bg-bg-alt',
      danger: 'bg-danger text-white hover:opacity-90',
    }
    
    const sizes = {
      sm: 'px-3 py-1.5 text-sm rounded-full',
      md: 'px-4 py-2 text-base rounded-full',
      lg: 'px-6 py-3 text-lg rounded-full',
    }
    
    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          "hover:shadow-sm hover:scale-[1.02]",
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent border-current" />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'